import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, Upload, X, FileText, CheckCircle2, ArrowLeft, Camera } from "lucide-react";
import { toast } from "sonner";

import Header from "@/components/layout/Header";
import Footer from "@/components/layout/Footer";
import SEO from "@/components/shared/SEO";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import LiveSelfieCapture from "@/components/shared/LiveSelfieCapture";
import { api, unwrap } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { COUNTRIES } from "@/lib/countries";
import { US_STATES } from "@/lib/us-states";

const LANGUAGES = [
  { code: "en", label: "English" }, { code: "es", label: "Español" },
  { code: "pt", label: "Português" }, { code: "fr", label: "Français" }, { code: "it", label: "Italiano" },
];

const EXPERIENCE = [
  { code: "<1", en: "Less than 1 year", es: "Menos de 1 año" },
  { code: "1-2", en: "1–2 years", es: "1–2 años" },
  { code: "3-5", en: "3–5 years", es: "3–5 años" },
  { code: "5+", en: "5+ years", es: "5+ años" },
];

function fileToSquareDataUrl(file, size = 512) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const side = Math.min(img.width, img.height);
        const sx = (img.width - side) / 2, sy = (img.height - side) / 2;
        const canvas = document.createElement("canvas");
        canvas.width = size; canvas.height = size;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(img, sx, sy, side, side, 0, 0, size, size);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const EMPTY = {
  full_name: "", email: "", phone: "", date_of_birth: "",
  country: "US", state: "", city: "",
  years_experience: "", specialties: [], languages: [], bio: "",
  photo_url: "", references: "",
  available_days: [], preferred_hours: "", weekly_commitment: "",
  has_reliable_transportation: null, transportation_type: "", transportation_other: "",
  availability_commitment_accepted: false,
  agree_terms: false, confirm_accuracy: false,
};

const DAYS = [
  { code: "monday", en: "Monday", es: "Lunes" },
  { code: "tuesday", en: "Tuesday", es: "Martes" },
  { code: "wednesday", en: "Wednesday", es: "Miércoles" },
  { code: "thursday", en: "Thursday", es: "Jueves" },
  { code: "friday", en: "Friday", es: "Viernes" },
  { code: "saturday", en: "Saturday", es: "Sábado" },
  { code: "sunday", en: "Sunday", es: "Domingo" },
];

const PREFERRED_HOURS = [
  { code: "mornings", en: "Mornings (7 AM – 12 PM)", es: "Mañanas (7 AM – 12 PM)" },
  { code: "midday", en: "Midday (11 AM – 3 PM)", es: "Mediodía (11 AM – 3 PM)" },
  { code: "afternoons", en: "Afternoons (12 PM – 5 PM)", es: "Tardes (12 PM – 5 PM)" },
  { code: "evenings", en: "Evenings (5 PM – 9 PM)", es: "Noches (5 PM – 9 PM)" },
  { code: "full_day", en: "Full day (7 AM – 5 PM)", es: "Todo el día (7 AM – 5 PM)" },
  { code: "flexible", en: "Flexible / Any time", es: "Flexible / Cualquier hora" },
];

const WEEKLY_COMMITMENT = [
  { code: "full_time", en: "Full-time (40+ hours/week)", es: "Tiempo completo (40+ horas/semana)" },
  { code: "part_time", en: "Part-time (20–39 hours/week)", es: "Medio tiempo (20–39 horas/semana)" },
  { code: "flexible", en: "Flexible / As needed (less than 20 hours/week)", es: "Flexible / Según necesidad (menos de 20 horas/semana)" },
];

const TRANSPORTATION_TYPE = [
  { code: "personal_vehicle", en: "Personal vehicle", es: "Vehículo propio" },
  { code: "family_friend", en: "Ride from family/friend", es: "Me lleva un familiar o amigo/a" },
  { code: "public", en: "Public transportation", es: "Transporte público" },
  { code: "other", en: "Other", es: "Otro" },
];

export default function CleanerApplyPage() {
  const { locale } = useI18n();
  const navigate = useNavigate();
  const L = (en, es) => (locale === "es" ? es : en);
  const [form, setForm] = useState(EMPTY);
  const [tasks, setTasks] = useState([]);
  const [idFile, setIdFile] = useState(null);
  const [selfieDataUrl, setSelfieDataUrl] = useState("");
  const [selfieBlob, setSelfieBlob] = useState(null);
  const [selfieOpen, setSelfieOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const photoRef = useRef(null);
  const idRef = useRef(null);

  useEffect(() => {
    api.get("/public/specialties")
      .then((r) => setTasks((unwrap(r) || []).map((s) => ({ code: s.slug, label_en: s.label_en, label_es: s.label_es }))))
      .catch(() => {});
  }, []);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggle = (key, val) => set({ [key]: form[key].includes(val) ? form[key].filter((x) => x !== val) : [...form[key], val] });

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 2 * 1024 * 1024) { toast.error(L("Photo must be under 2 MB", "La foto debe ser menor a 2 MB")); return; }
    try { set({ photo_url: await fileToSquareDataUrl(file) }); } catch { toast.error(L("Could not read image", "No pudimos leer la imagen")); }
  };

  const onId = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(L("ID must be under 5 MB", "El documento debe ser menor a 5 MB")); return; }
    const ok = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif", "application/pdf"];
    if (!ok.includes(file.type)) { toast.error(L("Only images or PDF allowed", "Solo imágenes o PDF")); return; }
    setIdFile(file);
  };

  const validate = () => {
    if (!form.full_name.trim()) return L("Full name is required", "El nombre es obligatorio");
    if (!form.email.trim()) return L("Email is required", "El email es obligatorio");
    if (!form.phone.trim()) return L("Phone is required", "El teléfono es obligatorio");
    if (!form.city.trim()) return L("City is required", "La ciudad es obligatoria");
    if (!form.years_experience) return L("Select your experience", "Elige tu experiencia");
    if (!form.photo_url) return L("A profile photo is required", "La foto de perfil es obligatoria");
    if (!idFile) return L("A government ID is required", "El documento de identidad es obligatorio");
    if (!selfieBlob) return L("A live selfie is required", "Una selfie en vivo es obligatoria");
    if (form.available_days.length === 0) return L("Please select at least one day", "Por favor selecciona al menos un día");
    if (!form.preferred_hours) return L("Please select your preferred hours", "Por favor selecciona tu horario preferido");
    if (!form.weekly_commitment) return L("Please select your weekly commitment", "Por favor selecciona tu compromiso semanal");
    if (form.has_reliable_transportation === null) return L("Please answer the transportation question", "Por favor responde la pregunta de transporte");
    if (form.has_reliable_transportation && !form.transportation_type) return L("Please select your transportation type", "Por favor selecciona tu tipo de transporte");
    if (form.has_reliable_transportation && form.transportation_type === "other" && !form.transportation_other.trim()) return L("Please describe your transportation", "Por favor describe tu transporte");
    if (!form.availability_commitment_accepted) return L("Please confirm your commitment to continue", "Por favor confirma tu compromiso para continuar");
    if (!form.agree_terms || !form.confirm_accuracy) return L("Please accept both agreements", "Acepta ambos acuerdos");
    return null;
  };

  const submit = async () => {
    const err = validate();
    if (err) { toast.error(err); return; }
    setSubmitting(true);
    try {
      // 1) Upload the private ID document
      const fd = new FormData();
      fd.append("file", idFile);
      const up = await api.post("/cleaners/upload-id", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const upData = unwrap(up);
      // 1.b) Upload the live selfie captured in-browser
      const fdSelfie = new FormData();
      fdSelfie.append("file", selfieBlob, "selfie.jpg");
      const upSelfie = await api.post("/cleaners/upload-selfie", fdSelfie, { headers: { "Content-Type": "multipart/form-data" } });
      const upSelfieData = unwrap(upSelfie);
      // 2) Submit the application
      await api.post("/cleaners/apply", {
        full_name: form.full_name, email: form.email, phone: form.phone, date_of_birth: form.date_of_birth,
        country: form.country, state: form.state, city: form.city,
        years_experience: form.years_experience, specialties: form.specialties, languages: form.languages,
        bio: form.bio, photo_url: form.photo_url, references: form.references,
        government_id_path: upData.path, government_id_content_type: upData.content_type,
        selfie_live_path: upSelfieData.path, selfie_live_content_type: upSelfieData.content_type,
        available_days: form.available_days, preferred_hours: form.preferred_hours,
        weekly_commitment: form.weekly_commitment, has_reliable_transportation: !!form.has_reliable_transportation,
        transportation_type: form.has_reliable_transportation ? form.transportation_type : null,
        transportation_other: form.has_reliable_transportation && form.transportation_type === "other" ? form.transportation_other : null,
        availability_commitment_accepted: form.availability_commitment_accepted,
        agree_terms: form.agree_terms, confirm_accuracy: form.confirm_accuracy, locale,
      });
      navigate("/work-with-us/application-received");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.message || L("Could not submit application", "No pudimos enviar la postulación"));
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAF9] flex flex-col" data-testid="cleaner-apply-page">
      <SEO title={L("Apply — Ever Clean Home", "Postulación — Ever Clean Home")} description={L("Apply to join our cleaning team.", "Postúlate a nuestro equipo de limpieza.")} noindex />
      <Header />

      <div className="max-w-3xl mx-auto px-4 md:px-8 w-full py-10 md:py-14">
        <button onClick={() => navigate("/work-with-us")} className="text-sm text-[#4B5563] hover:text-[#0EA5A4] flex items-center gap-1 mb-4" data-testid="apply-back">
          <ArrowLeft className="w-4 h-4" /> {L("Back", "Atrás")}
        </button>
        <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">{L("Apply to join the team", "Postúlate al equipo")}</h1>
        <p className="mt-3 text-[#4B5563]">{L("Tell us about yourself. It takes about 5 minutes.", "Cuéntanos sobre ti. Toma unos 5 minutos.")}</p>

        {/* Personal info */}
        <Card className="rounded-2xl p-6 border border-[#E5E7EB] mt-8 space-y-4">
          <h2 className="font-display font-bold text-lg">{L("Personal info", "Información personal")}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2"><Label className="text-sm">{L("Full name", "Nombre completo")} *</Label>
              <Input value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} className="rounded-xl mt-1" data-testid="apply-full-name" /></div>
            <div><Label className="text-sm">{L("Email", "Email")} *</Label>
              <Input type="email" value={form.email} onChange={(e) => set({ email: e.target.value })} className="rounded-xl mt-1" data-testid="apply-email" /></div>
            <div><Label className="text-sm">{L("Phone", "Teléfono")} *</Label>
              <Input value={form.phone} onChange={(e) => set({ phone: e.target.value })} className="rounded-xl mt-1" data-testid="apply-phone" /></div>
            <div><Label className="text-sm">{L("Date of birth", "Fecha de nacimiento")}</Label>
              <Input type="date" value={form.date_of_birth} onChange={(e) => set({ date_of_birth: e.target.value })} className="rounded-xl mt-1" data-testid="apply-dob" /></div>
            <div><Label className="text-sm">{L("Country", "País")} *</Label>
              <select value={form.country} onChange={(e) => set({ country: e.target.value, state: "" })} className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="apply-country">
                {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{locale === "es" ? c.name_es : c.name_en}</option>)}
              </select></div>
            <div><Label className="text-sm">{L("State / Province", "Estado / Provincia")}</Label>
              {form.country === "US" ? (
                <select value={form.state} onChange={(e) => set({ state: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="apply-state-select">
                  <option value="">{L("Select…", "Elige…")}</option>
                  {US_STATES.map((s) => <option key={s.code} value={s.name}>{s.name}</option>)}
                </select>
              ) : (
                <Input value={form.state} onChange={(e) => set({ state: e.target.value })} className="rounded-xl mt-1" data-testid="apply-state" />
              )}</div>
            <div><Label className="text-sm">{L("City", "Ciudad")} *</Label>
              <Input value={form.city} onChange={(e) => set({ city: e.target.value })} className="rounded-xl mt-1" data-testid="apply-city" /></div>
          </div>
        </Card>

        {/* Professional info */}
        <Card className="rounded-2xl p-6 border border-[#E5E7EB] mt-5 space-y-4">
          <h2 className="font-display font-bold text-lg">{L("Professional info", "Información profesional")}</h2>
          <div>
            <Label className="text-sm">{L("Years of cleaning experience", "Años de experiencia en limpieza")} *</Label>
            <select value={form.years_experience} onChange={(e) => set({ years_experience: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="apply-experience">
              <option value="">{L("Select…", "Elige…")}</option>
              {EXPERIENCE.map((x) => <option key={x.code} value={x.code}>{L(x.en, x.es)}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-sm">{L("Specialties", "Especialidades")}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {tasks.length === 0 ? <span className="text-xs text-[#9CA3AF]">—</span> :
                tasks.map((tk) => (
                  <Chip key={tk.code} active={form.specialties.includes(tk.code)} onClick={() => toggle("specialties", tk.code)} testid={`apply-spec-${tk.code}`}>
                    {locale === "es" ? (tk.label_es || tk.code) : (tk.label_en || tk.code)}
                  </Chip>
                ))}
            </div>
          </div>
          <div>
            <Label className="text-sm">{L("Languages spoken", "Idiomas")}</Label>
            <div className="flex flex-wrap gap-2 mt-2">
              {LANGUAGES.map((l) => (
                <Chip key={l.code} active={form.languages.includes(l.code)} onClick={() => toggle("languages", l.code)} testid={`apply-lang-${l.code}`}>{l.label}</Chip>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-sm">{L("Bio", "Biografía")}</Label>
            <Textarea rows={3} value={form.bio} onChange={(e) => set({ bio: e.target.value })} className="rounded-xl mt-1"
              placeholder={L("Tell customers about yourself — 2-3 sentences", "Contales a los clientes sobre ti — 2 o 3 frases")} data-testid="apply-bio" />
          </div>
        </Card>

        {/* Availability & Transportation */}
        <Card className="rounded-2xl p-6 border border-[#E5E7EB] mt-5 space-y-5" data-testid="apply-availability-section">
          <h2 className="font-display font-bold text-lg">{L("Availability & Transportation", "Disponibilidad y transporte")}</h2>

          <div>
            <Label className="text-sm">{L("Which days can you work?", "¿Qué días puedes trabajar?")} *</Label>
            <p className="text-xs text-[#9CA3AF] mb-2">{L("Select all that apply", "Marca todos los que apliquen")}</p>
            <div className="flex flex-wrap gap-2">
              {DAYS.map((d) => (
                <Chip key={d.code} active={form.available_days.includes(d.code)} onClick={() => toggle("available_days", d.code)} testid={`apply-day-${d.code}`}>
                  {L(d.en, d.es)}
                </Chip>
              ))}
            </div>
          </div>

          <div>
            <Label className="text-sm">{L("What hours can you typically work?", "¿En qué horario puedes trabajar?")} *</Label>
            <select value={form.preferred_hours} onChange={(e) => set({ preferred_hours: e.target.value })} className="mt-1 w-full h-10 rounded-xl border border-[#E5E7EB] px-3 text-sm bg-white" data-testid="apply-preferred-hours">
              <option value="">{L("Select…", "Elige…")}</option>
              {PREFERRED_HOURS.map((h) => <option key={h.code} value={h.code}>{L(h.en, h.es)}</option>)}
            </select>
          </div>

          <div>
            <Label className="text-sm">{L("How many hours per week can you commit?", "¿Cuántas horas por semana puedes comprometerte?")} *</Label>
            <RadioGroup value={form.weekly_commitment} onValueChange={(v) => set({ weekly_commitment: v })} className="mt-2 space-y-2" data-testid="apply-weekly-commitment">
              {WEEKLY_COMMITMENT.map((c) => (
                <label key={c.code} className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer">
                  <RadioGroupItem value={c.code} data-testid={`apply-commitment-${c.code}`} /> {L(c.en, c.es)}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div>
            <Label className="text-sm">{L("Do you have reliable transportation to commute to jobs?", "¿Tienes transporte confiable para llegar a los trabajos?")} *</Label>
            <RadioGroup
              value={form.has_reliable_transportation === null ? "" : (form.has_reliable_transportation ? "yes" : "no")}
              onValueChange={(v) => set({ has_reliable_transportation: v === "yes", transportation_type: v === "no" ? "" : form.transportation_type })}
              className="mt-2 flex gap-6" data-testid="apply-has-transportation"
            >
              <label className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer"><RadioGroupItem value="yes" data-testid="apply-transportation-yes" /> {L("Yes", "Sí")}</label>
              <label className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer"><RadioGroupItem value="no" data-testid="apply-transportation-no" /> {L("No", "No")}</label>
            </RadioGroup>
          </div>

          {form.has_reliable_transportation && (
            <div>
              <Label className="text-sm">{L("What type of transportation will you use?", "¿Qué tipo de transporte vas a usar?")} *</Label>
              <RadioGroup value={form.transportation_type} onValueChange={(v) => set({ transportation_type: v })} className="mt-2 space-y-2" data-testid="apply-transportation-type">
                {TRANSPORTATION_TYPE.map((t) => (
                  <label key={t.code} className="flex items-center gap-2 text-sm text-[#4B5563] cursor-pointer">
                    <RadioGroupItem value={t.code} data-testid={`apply-transportation-type-${t.code}`} /> {L(t.en, t.es)}
                  </label>
                ))}
              </RadioGroup>
              {form.transportation_type === "other" && (
                <Input value={form.transportation_other} onChange={(e) => set({ transportation_other: e.target.value })} className="rounded-xl mt-2"
                  placeholder={L("Describe your transportation", "Describe tu transporte")} data-testid="apply-transportation-other" />
              )}
            </div>
          )}

          <label className="flex items-start gap-3 cursor-pointer pt-2 border-t border-[#E5E7EB]" data-testid="apply-availability-commitment-label">
            <Checkbox checked={form.availability_commitment_accepted} onCheckedChange={(v) => set({ availability_commitment_accepted: !!v })} className="mt-0.5" data-testid="apply-availability-commitment" />
            <span className="text-sm text-[#4B5563]">
              {L(
                "I confirm the availability I selected above and understand that Ever Clean Home will assign me jobs based on these hours and days. I commit to being present, on time, and prepared to work when accepting an assignment.",
                "Confirmo la disponibilidad que seleccioné arriba y entiendo que Ever Clean Home me va a asignar trabajos basándose en estos horarios y días. Me comprometo a estar presente, a tiempo, y preparado/a para trabajar cuando acepte una asignación."
              )}
            </span>
          </label>
        </Card>

        {/* Verification */}
        <Card className="rounded-2xl p-6 border border-[#E5E7EB] mt-5 space-y-5">
          <h2 className="font-display font-bold text-lg">{L("Verification", "Verificación")}</h2>

          <div>
            <Label className="text-sm">{L("Profile photo", "Foto de perfil")} *</Label>
            <p className="text-xs text-[#9CA3AF] mb-2">{L("A clear photo of your face. Square crop, max 2 MB.", "Una foto clara de tu rostro. Recorte cuadrado, máx 2 MB.")}</p>
            <div className="flex items-center gap-4">
              <PhotoPreview src={form.photo_url} />
              <div className="flex gap-2">
                <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={onPhoto} data-testid="apply-photo-input" />
                <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => photoRef.current?.click()} data-testid="apply-photo-btn">
                  <Upload className="w-4 h-4 mr-2" /> {L("Upload photo", "Subir foto")}
                </Button>
                {form.photo_url && <Button type="button" variant="ghost" size="sm" className="rounded-xl text-rose-600" onClick={() => set({ photo_url: "" })}><X className="w-4 h-4" /></Button>}
              </div>
            </div>
          </div>

          <div>
            <Label className="text-sm">{L("Government ID", "Documento de identidad")} *</Label>
            <p className="text-xs text-[#9CA3AF] mb-2">{L("Kept private — only our team sees it for verification. Image or PDF, max 5 MB.", "Se mantiene privado — solo nuestro equipo lo ve para verificación. Imagen o PDF, máx 5 MB.")}</p>
            <input ref={idRef} type="file" accept="image/*,application/pdf" className="hidden" onChange={onId} data-testid="apply-id-input" />
            {idFile ? (
              <div className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#F5F5F4] px-4 py-3" data-testid="apply-id-selected">
                <FileText className="w-5 h-5 text-[#0EA5A4]" />
                <span className="text-sm font-medium truncate flex-1">{idFile.name}</span>
                <Button type="button" variant="ghost" size="sm" className="rounded-xl text-rose-600" onClick={() => setIdFile(null)}><X className="w-4 h-4" /></Button>
              </div>
            ) : (
              <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => idRef.current?.click()} data-testid="apply-id-btn">
                <Upload className="w-4 h-4 mr-2" /> {L("Upload ID", "Subir documento")}
              </Button>
            )}
          </div>

          <div>
            <Label className="text-sm">{L("Live selfie", "Selfie en vivo")} *</Label>
            <p className="text-xs text-[#9CA3AF] mb-2">{L("Take a quick selfie with your camera so our team can compare it with your ID. Photos from the gallery are not accepted.",
                                                          "Tómate una selfie con tu cámara para que nuestro equipo pueda compararla con tu documento. No se aceptan fotos de la galería.")}</p>
            {selfieDataUrl ? (
              <div className="flex items-center gap-3 rounded-xl border border-[#E5E7EB] bg-[#F5F5F4] px-3 py-2" data-testid="apply-selfie-selected">
                <img src={selfieDataUrl} alt="selfie preview" className="w-12 h-12 rounded-lg object-cover" />
                <span className="text-sm font-medium flex-1">{L("Selfie captured", "Selfie capturada")}</span>
                <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => setSelfieOpen(true)} data-testid="apply-selfie-retake">
                  <Camera className="w-4 h-4 mr-2" /> {L("Retake", "Repetir")}
                </Button>
                <Button type="button" variant="ghost" size="sm" className="rounded-xl text-rose-600" onClick={() => { setSelfieBlob(null); setSelfieDataUrl(""); }}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            ) : (
              <Button type="button" variant="outline" size="sm" className="rounded-xl" onClick={() => setSelfieOpen(true)} data-testid="apply-selfie-btn">
                <Camera className="w-4 h-4 mr-2" /> {L("Take a live selfie", "Tomar selfie en vivo")}
              </Button>
            )}
          </div>

          <div>
            <Label className="text-sm">{L("References (optional)", "Referencias (opcional)")}</Label>
            <Textarea rows={2} value={form.references} onChange={(e) => set({ references: e.target.value })} className="rounded-xl mt-1"
              placeholder={L("Contacts who can vouch for your work", "Contactos que puedan dar referencias de tu trabajo")} data-testid="apply-references" />
          </div>
        </Card>

        {/* Agreement */}
        <Card className="rounded-2xl p-6 border border-[#E5E7EB] mt-5 space-y-3">
          <label className="flex items-start gap-3 cursor-pointer" data-testid="apply-agree-terms-label">
            <Checkbox checked={form.agree_terms} onCheckedChange={(v) => set({ agree_terms: !!v })} className="mt-0.5" data-testid="apply-agree-terms" />
            <span className="text-sm text-[#4B5563]">
              {L("I have read and agree to the ", "He leído y acepto el ")}
              <a
                href="/cleaner-agreement"
                target="_blank"
                rel="noreferrer noopener"
                className="font-semibold text-[#0EA5A4] hover:underline"
                data-testid="apply-agree-terms-link"
              >
                {L("Cleaner Code of Conduct", "Código de Conducta del Limpiador")}
              </a>
              .
            </span>
          </label>
          <label className="flex items-start gap-3 cursor-pointer">
            <Checkbox checked={form.confirm_accuracy} onCheckedChange={(v) => set({ confirm_accuracy: !!v })} className="mt-0.5" data-testid="apply-confirm-accuracy" />
            <span className="text-sm text-[#4B5563]">{L("I confirm that all information is accurate.", "Confirmo que toda la información es correcta.")}</span>
          </label>
        </Card>

        <Button onClick={submit} disabled={submitting} className="mt-6 w-full h-12 rounded-xl bg-[#0EA5A4] hover:bg-[#0F766E] text-white font-bold text-base" data-testid="apply-submit">
          {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : <><CheckCircle2 className="w-5 h-5 mr-2" /> {L("Submit application", "Enviar postulación")}</>}
        </Button>
      </div>

      <LiveSelfieCapture
        open={selfieOpen}
        onOpenChange={(o) => { if (!o) setSelfieOpen(false); }}
        title={L("Take a live selfie", "Tómate una selfie en vivo")}
        description={L("Center your face inside the frame and tap Capture.",
                       "Centra tu cara dentro del marco y toca Capturar.")}
        onCapture={(blob, dataUrl) => { setSelfieBlob(blob); setSelfieDataUrl(dataUrl); setSelfieOpen(false); }}
      />

      <Footer />
    </div>
  );
}

function PhotoPreview({ src }) {
  if (src) return <img src={src} alt="" width={80} height={80} className="w-20 h-20 rounded-full object-cover" />;
  return <div className="w-20 h-20 rounded-full bg-[#0EA5A4]/10 grid place-items-center text-[#0F766E]"><Upload className="w-6 h-6" /></div>;
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${active ? "bg-[#0EA5A4] text-white border-[#0EA5A4]" : "bg-white text-[#4B5563] border-[#E5E7EB] hover:border-[#0EA5A4]"}`}>
      {children}
    </button>
  );
}
